import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Radio,
  Loader2,
  Hand,
  Mic,
  MicOff,
  LogOut,
  PhoneOff,
  ArrowUpCircle,
  ArrowDownCircle,
  Volume2,
  Crown,
  Video,
  VideoOff,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStreamAudio } from "@/lib/useStreamAudio";
import { Avatar } from "@/components/common/Avatar";
import { VerifiedCheck } from "@/components/common/TrustBadge";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

function PersonChip({ person, isHostSlot, canPromote, canDemote, onPromote, onDemote, busy, speaking }) {
  return (
    <div className="flex flex-col items-center gap-1.5" data-testid={`room-person-${person.id}`}>
      <div className="relative">
        <span
          className={cn(
            "block rounded-full transition-all",
            speaking ? "ring-2 ring-bloq-mint ring-offset-2 ring-offset-bloq-s1" : "ring-0",
          )}
          data-testid={speaking ? `speaking-${person.id}` : undefined}
        >
          <Avatar user={person} size="md" />
        </span>
        {person.hand_raised && (
          <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-bloq-bg" data-testid={`hand-${person.id}`}>
            <Hand className="h-3 w-3" />
          </span>
        )}
        {isHostSlot && (
          <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-bloq-purple text-white">
            <Crown className="h-3 w-3" />
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 text-center">
        <span className="max-w-[80px] truncate text-xs font-medium">{person.display_name}</span>
        {person.verified && <VerifiedCheck size={11} />}
      </div>
      {canPromote && (
        <button onClick={() => onPromote(person.id)} disabled={busy} className="flex items-center gap-1 rounded-full bg-bloq-mint/15 px-2 py-0.5 text-[10px] font-bold text-bloq-mint disabled:opacity-50" data-testid={`promote-${person.id}`}>
          <ArrowUpCircle className="h-3 w-3" /> Stage
        </button>
      )}
      {canDemote && (
        <button onClick={() => onDemote(person.id)} disabled={busy} className="flex items-center gap-1 rounded-full bg-white/8 px-2 py-0.5 text-[10px] font-bold text-bloq-muted disabled:opacity-50" data-testid={`demote-${person.id}`}>
          <ArrowDownCircle className="h-3 w-3" /> Move
        </button>
      )}
    </div>
  );
}

export function RoomStage({ bloq, open, onOpenChange }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [roomId, setRoomId] = useState(null);
  const [title, setTitle] = useState("");
  const [joinInfo, setJoinInfo] = useState(null); // { stream: {api_key, token, call_type, call_id}, role, can_record }

  useEffect(() => {
    if (open) {
      setRoomId(bloq?.active_room?.id || null);
      setTitle("");
    } else {
      setJoinInfo(null);
      setRoomId(null);
    }
  }, [open, bloq?.active_room?.id]);

  const canPublish = !!joinInfo && joinInfo.role !== "listener";
  const { connected, micOn, toggleMic, speakingIds, error: lkError } = useStreamAudio({
    stream: joinInfo?.stream,
    canPublish,
  });

  useEffect(() => {
    if (lkError === "mic") toast.error("Enable microphone access to speak");
    else if (lkError === "connect") toast.error("Couldn't connect to live audio");
  }, [lkError]);

  const { data: room, isFetching } = useQuery({
    queryKey: ["room", roomId],
    queryFn: () => api.getRoom(roomId),
    enabled: open && !!roomId,
    refetchInterval: open && roomId ? 4000 : false,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["room", roomId] });
    qc.invalidateQueries({ queryKey: ["bloq", bloq?.id] });
    qc.invalidateQueries({ queryKey: ["bloqs"] });
  };

  const startMut = useMutation({
    mutationFn: () => api.startRoom(bloq.id, title.trim() || null),
    onSuccess: (res) => {
      const rid = res?.room?.id;
      setRoomId(rid);
      invalidate();
      toast.success("Room is live");
      if (rid) joinMut.mutate(rid); // fetch host audio token
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't start room"),
  });

  const joinMut = useMutation({
    mutationFn: (rid) => api.joinRoom(rid || roomId),
    onSuccess: (res) => {
      if (res?.stream?.token) {
        setJoinInfo({ stream: res.stream, role: res.role, can_record: res.can_record });
      }
      invalidate();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't join"),
  });
  const leaveMut = useMutation({ mutationFn: () => api.leaveRoom(roomId), onSuccess: () => { setJoinInfo(null); invalidate(); toast.success("Left the room"); }, onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't leave") });
  const handMut = useMutation({ mutationFn: () => api.raiseHand(roomId), onSuccess: () => { invalidate(); toast.success("Hand raised"); }, onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't raise hand") });
  const promoteMut = useMutation({ mutationFn: (uid) => api.promoteSpeaker(roomId, uid), onSuccess: invalidate, onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't promote") });
  const demoteMut = useMutation({ mutationFn: (uid) => api.demoteSpeaker(roomId, uid), onSuccess: invalidate, onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't move") });
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [bursts, setBursts] = useState([]); // floating reaction emojis
  const reactMut = useMutation({
    mutationFn: (emoji) => api.roomReaction(roomId, emoji),
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't send reaction"),
  });
  const sendReaction = (emoji) => {
    reactMut.mutate(emoji);
    const id = Date.now() + Math.random();
    setBursts((b) => [...b, { id, emoji }]);
    setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 1300);
  };
  const videoMut = useMutation({
    mutationFn: (enable) => api.roomVideo(roomId, enable),
    onSuccess: (_r, enable) => { setVideoEnabled(enable); toast.success(enable ? "Video enabled for the room" : "Video turned off"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't toggle video"),
  });
  const endMut = useMutation({
    mutationFn: () => api.endRoom(roomId),
    onSuccess: () => { setJoinInfo(null); invalidate(); onOpenChange(false); toast.success("Room ended"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't end room"),
  });

  const me = user?.id;
  const speakers = room?.speakers || [];
  const listeners = room?.listeners || [];
  const isHost = room?.host_id === me;
  const amSpeaker = speakers.some((s) => s.id === me);
  const amListener = listeners.some((l) => l.id === me);
  const joined = amSpeaker || amListener;
  const busy = joinMut.isPending || leaveMut.isPending || handMut.isPending || promoteMut.isPending || demoteMut.isPending || endMut.isPending;
  const meListener = listeners.find((l) => l.id === me);

  // Stage role changed (promote/demote) → keep the local publish state in sync with the server roster.
  useEffect(() => {
    if (!joinInfo) return;
    const role = isHost ? "host" : amSpeaker ? "speaker" : "listener";
    if (role !== joinInfo.role) setJoinInfo((j) => (j ? { ...j, role } : j));
  }, [isHost, amSpeaker, joinInfo]);


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-white/10 bg-bloq-s1 text-white" data-testid="room-stage">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Radio className="h-5 w-5 text-bloq-mint" />
            {room?.title || `${bloq?.name || "BLOQ"} Room`}
          </DialogTitle>
          <DialogDescription className="text-bloq-muted">
            {roomId ? "Live audio room — voice powered by encrypted WebRTC." : "Start a live audio room for this bloq."}
          </DialogDescription>
        </DialogHeader>

        {!roomId ? (
          <div className="space-y-4">
            {bloq?.can_start_room ? (
              <>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Room title (optional)"
                  className="h-11 w-full rounded-xl border border-white/12 bg-bloq-bg px-4 text-sm text-white focus:border-bloq-mint focus:outline-none"
                  data-testid="room-title-input"
                />
                <button
                  onClick={() => startMut.mutate()}
                  disabled={startMut.isPending}
                  className="flex w-full items-center justify-center gap-2 rounded-full bloq-gradient py-3 text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-60"
                  data-testid="start-room-btn"
                >
                  {startMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radio className="h-4 w-4" />}
                  Start room
                </button>
              </>
            ) : (
              <div className="rounded-xl border border-white/8 bg-bloq-s2 px-4 py-8 text-center text-sm text-bloq-muted" data-testid="room-none">
                No live room right now. Only bloq owners can start a room.
              </div>
            )}
          </div>
        ) : !room ? (
          <div className="flex justify-center py-10 text-bloq-muted"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center gap-2 text-xs">
              <span className="flex items-center gap-1 rounded-full border border-destructive/40 bg-destructive/15 px-2 py-0.5 font-bold uppercase text-destructive">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-destructive" /> Live
              </span>
              <span className="text-bloq-muted">{room.speaker_count} on stage · {room.listener_count} listening</span>
              {isFetching && <Loader2 className="h-3 w-3 animate-spin text-bloq-muted" />}
            </div>

            <div>
              <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-bloq-muted"><Mic className="h-3.5 w-3.5" /> On stage</div>
              <div className="flex flex-wrap gap-4">
                {speakers.map((s) => (
                  <PersonChip
                    key={s.id}
                    person={s}
                    isHostSlot={s.id === room.host_id}
                    canDemote={isHost && s.id !== room.host_id}
                    onDemote={demoteMut.mutate}
                    busy={busy}
                    speaking={speakingIds.includes(s.id)}
                  />
                ))}
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-bloq-muted"><Volume2 className="h-3.5 w-3.5" /> Listeners</div>
              {listeners.length === 0 ? (
                <p className="text-xs text-bloq-muted">No listeners yet.</p>
              ) : (
                <div className="flex flex-wrap gap-4">
                  {listeners.map((l) => (
                    <PersonChip
                      key={l.id}
                      person={l}
                      canPromote={isHost && l.hand_raised}
                      onPromote={promoteMut.mutate}
                      busy={busy}
                      speaking={speakingIds.includes(l.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            <div
              className={cn(
                "flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-center text-[11px] font-semibold",
                joined && connected
                  ? "border-bloq-mint/30 bg-bloq-mint/10 text-bloq-mint"
                  : joined
                    ? "border-white/8 bg-bloq-s2 text-bloq-muted"
                    : "border-white/8 bg-bloq-s2 text-bloq-muted",
              )}
              data-testid="room-audio-note"
            >
              {joined && connected ? (
                <>
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-bloq-mint" />
                  {amSpeaker || isHost ? "You're live on stage" : "You're listening live"}
                </>
              ) : joined ? (
                <><Loader2 className="h-3 w-3 animate-spin" /> Connecting audio…</>
              ) : (
                <><Volume2 className="mr-0.5 inline h-3 w-3" /> Join to hear the live audio.</>
              )}
            </div>

            {joined && (
              <div className="relative">
                <div className="flex items-center justify-center gap-2" data-testid="room-reactions-bar">
                  {["👏", "❤️", "🔥", "😂", "🎉", "💯"].map((e) => (
                    <button
                      key={e}
                      onClick={() => sendReaction(e)}
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-white/8 text-lg transition-transform hover:scale-110 active:scale-95"
                      data-testid={`room-react-${e}`}
                    >
                      {e}
                    </button>
                  ))}
                </div>
                <div className="pointer-events-none absolute inset-x-0 -top-10 flex justify-center">
                  {bursts.map((b) => (
                    <span key={b.id} className="animate-fade-up px-1 text-2xl" style={{ animationDuration: "1.2s" }}>{b.emoji}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {!joined && (
                <button onClick={() => joinMut.mutate()} disabled={busy} className="flex flex-1 items-center justify-center gap-2 rounded-full bloq-gradient py-2.5 text-sm font-bold text-bloq-bg disabled:opacity-60" data-testid="room-join-btn">
                  {joinMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join room"}
                </button>
              )}
              {joined && (isHost || amSpeaker) && (
                <button
                  onClick={toggleMic}
                  disabled={!connected}
                  className={cn(
                    "flex flex-1 items-center justify-center gap-2 rounded-full py-2.5 text-sm font-bold transition-colors disabled:opacity-50",
                    micOn ? "bloq-gradient text-bloq-bg" : "border border-white/12 text-bloq-muted hover:text-white",
                  )}
                  data-testid="room-mic-btn"
                >
                  {micOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                  {micOn ? "Mute" : "Unmute"}
                </button>
              )}
              {joined && (isHost || amSpeaker) && (
                <button
                  onClick={() => videoMut.mutate(!videoEnabled)}
                  disabled={videoMut.isPending}
                  className={cn(
                    "flex flex-1 items-center justify-center gap-2 rounded-full py-2.5 text-sm font-bold transition-colors disabled:opacity-50",
                    videoEnabled ? "border border-bloq-purple/50 bg-bloq-purple/15 text-bloq-purple" : "border border-white/12 text-bloq-muted hover:text-white",
                  )}
                  data-testid="room-video-btn"
                >
                  {videoMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : videoEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                  {videoEnabled ? "Video on" : "Go video"}
                </button>
              )}
              {amListener && (
                <button onClick={() => handMut.mutate()} disabled={busy || meListener?.hand_raised} className="flex flex-1 items-center justify-center gap-2 rounded-full border border-amber-500/50 bg-amber-500/15 py-2.5 text-sm font-bold text-amber-400 disabled:opacity-50" data-testid="room-hand-btn">
                  <Hand className="h-4 w-4" /> {meListener?.hand_raised ? "Hand raised" : "Raise hand"}
                </button>
              )}
              {joined && !isHost && (
                <button onClick={() => { leaveMut.mutate(); onOpenChange(false); }} disabled={busy} className="flex flex-1 items-center justify-center gap-2 rounded-full border border-white/12 py-2.5 text-sm font-semibold text-bloq-muted hover:text-white disabled:opacity-50" data-testid="room-leave-btn">
                  <LogOut className="h-4 w-4" /> Leave
                </button>
              )}
              {isHost && (
                <button onClick={() => endMut.mutate()} disabled={busy} className="flex flex-1 items-center justify-center gap-2 rounded-full border border-destructive/50 bg-destructive/15 py-2.5 text-sm font-bold text-destructive disabled:opacity-50" data-testid="room-end-btn">
                  <PhoneOff className="h-4 w-4" /> End room
                </button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
